import React, { Fragment, type ReactNode, useEffect } from 'react';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import type { ExtensionParams, Parameters } from '@atlaskit/editor-common/extensions';
import UFOSegment from '@atlaskit/react-ufo/segment';

import { useProductDependencies } from '../../controllers/use-product-dependencies';
import { ExtensionMode } from '../../types';
import { NoExtensionData } from '../error-states/no-extension-data';
import { GenericErrorPage } from '../generic-error-page';
import { HubErrorBoundary } from '../hub-error-boundary';

import { EXTENSIONS_CRITICAL_FOR_PAGE_LOAD } from './constants';

export type HubExtensionsWrapperProps<T extends Parameters> = {
	analyticsAttributes?: { [key: string]: string | boolean | number };
	children?: ReactNode;
	contentId: string;
	extensionNode?: ExtensionParams<T>;
	mode?: ExtensionMode;
	showNoDataState?: boolean;
};

export const HubExtensionsWrapper = <T extends Parameters>({
	extensionNode,
	contentId,
	analyticsAttributes,
	children,
	showNoDataState,
	mode = ExtensionMode.RENDERER,
	packageName,
}: HubExtensionsWrapperProps<T> & { packageName: string }) => {
	const { analyticsContext, errorBoundaryComponent, experienceStartComponent } =
		useProductDependencies();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { extensionKey, parameters } = extensionNode || {};
	const hasParameters = !showNoDataState || parameters;

	const ExperienceStart = experienceStartComponent || Fragment;
	const ErrorBoundaryComponent = errorBoundaryComponent || HubErrorBoundary;
	const errorBoundaryProps = errorBoundaryComponent
		? ({} as any)
		: {
				id: extensionKey ?? contentId,
				packageName,
				fallback: <GenericErrorPage />,
			};

	useEffect(() => {
		if (mode === ExtensionMode.RENDERER) {
			createAnalyticsEvent({
				type: 'sendTrackEvent',
				data: {
					action: 'viewed',
					actionSubject: 'macro',
					actionSubjectId: extensionKey,
					source: 'viewPageScreen',
					attributes: {
						...analyticsAttributes,
						...analyticsContext,
						hasParameters: !!parameters,
						contentId,
					},
				},
			}).fire();
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [mode]);

	return (
		<UFOSegment name={extensionKey || 'unknown-hub-extension'}>
			<ErrorBoundaryComponent
				{...errorBoundaryProps}
				shouldForceError={EXTENSIONS_CRITICAL_FOR_PAGE_LOAD.has(extensionKey || '')}
			>
				<ExperienceStart />
				{hasParameters ? children : <NoExtensionData source={extensionKey} />}
			</ErrorBoundaryComponent>
		</UFOSegment>
	);
};
